import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';

export default function AdminTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newRole, setNewRole] = useState('member');
  const [adding, setAdding] = useState(false);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/admin/users');
      setUsers(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newEmail.trim() || !newName.trim()) return;

    try {
      setAdding(true);
      await apiCall('/api/admin/users', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail, name: newName, role: newRole }),
      });
      setUsers([...users, { email: newEmail, name: newName, role: newRole, enabled: 1 }].sort((a, b) => a.email.localeCompare(b.email)));
      setNewEmail('');
      setNewName('');
      setNewRole('member');
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  const handleToggleUser = async (email, currentEnabled) => {
    try {
      await apiCall(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !currentEnabled }),
      });
      setUsers(
        users.map((u) => (u.email === email ? { ...u, enabled: !currentEnabled ? 1 : 0 } : u))
      );
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteUser = async (email) => {
    if (!confirm(`Remove ${email} from access?`)) return;

    try {
      await apiCall(`/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      setUsers(users.filter((u) => u.email !== email));
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading)
    return <div className="flex items-center justify-center py-12">Loading users...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold font-headline mb-4">Admin: Manage Access</h2>
        <p className="text-on-surface-variant mb-4">
          Control who can access the Strategy Hub. Only admins can add/remove users.
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Add User Form */}
      <div className="card p-4 sm:p-6">
        <h3 className="font-bold font-headline text-lg mb-4">Add New User</h3>
        <form onSubmit={handleAddUser} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Email Address</label>
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-full px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="John Doe"
                className="w-full px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Role</label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <button
            type="submit"
            disabled={adding || !newEmail.trim() || !newName.trim()}
            className="btn-primary disabled:opacity-50"
          >
            {adding ? 'Adding...' : 'Add User'}
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="card p-4 sm:p-6">
        <h3 className="font-bold font-headline text-lg mb-4">
          Authorised Users ({users.length})
        </h3>

        {users.length === 0 ? (
          <p className="text-on-surface-variant">No users yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-outline-variant">
                  <th className="text-left py-2 px-2">Name</th>
                  <th className="text-left py-2 px-2">Email</th>
                  <th className="text-left py-2 px-2">Role</th>
                  <th className="text-left py-2 px-2">Status</th>
                  <th className="text-left py-2 px-2">Added</th>
                  <th className="text-left py-2 px-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.email} className="border-b border-outline-variant hover:bg-surface-container">
                    <td className="py-2 px-2 font-medium">{user.name}</td>
                    <td className="py-2 px-2 text-xs text-on-surface-variant">{user.email}</td>
                    <td className="py-2 px-2">
                      <span
                        className={`text-xs px-2 py-1 rounded font-medium ${
                          user.role === 'admin'
                            ? 'bg-primary text-on-primary'
                            : 'bg-surface-container text-on-surface'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="py-2 px-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          user.enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.enabled ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="py-2 px-2 text-xs text-on-surface-variant">
                      {new Date(user.added_at).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-2 space-x-2">
                      <button
                        onClick={() => handleToggleUser(user.email, user.enabled)}
                        className="text-primary hover:underline text-xs"
                      >
                        {user.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(user.email)}
                        className="text-error hover:underline text-xs"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
